import { Box } from "@material-ui/core";
import React, { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { convertDatoCmsAssetUrlToCloudfrontUrl } from "../../helpers/cdn";
import useCountry from "../../hooks/use-country";
import useSubcountry from "../../hooks/use-subcountry";

const POD_VIDEO_LOOP_DELAY_MS = 3500; // Loop pod video every 3.5s

type Props = {
  className?: string;
  style?: React.CSSProperties;
};

const VIDEO_ASSETS = {
  UNLOCK: {
    mp4: "https://www.datocms-assets.com/15671/1616419063-engineered-04-desk5-1.mov",
    webm: "https://www.datocms-assets.com/15671/1616419054-engineered-04-desk5.webm"
  },
  SCANTHIS: {
    mp4: "https://www.datocms-assets.com/15671/1670426869-pod-spin-ohio.mov",
    webm: "https://www.datocms-assets.com/15671/1670426872-pod-spin-ohio.webm"
  },
  CANADA_THC: {
    mp4: "https://www.datocms-assets.com/15671/1670426859-pod-spin-canada.mov",
    webm: "https://www.datocms-assets.com/15671/1670426866-pod-spin-canada.webm"
  }
};

const PodVideo = ({ className, style }: Props) => {
  // Detect which assets to show based on current country/subcountry
  const country = useCountry();
  const { subcountry } = useSubcountry();
  let assets = VIDEO_ASSETS.UNLOCK;
  if (country === "US" && ["OH", "WA"].includes(subcountry)) {
    assets = VIDEO_ASSETS.SCANTHIS;
  } else if (country === "CA") {
    assets = VIDEO_ASSETS.CANADA_THC;
  }

  // Pod video
  const videoRef = useRef<HTMLVideoElement>();
  const [inViewRef, podInView] = useInView();

  /**
   * Loop video with delay
   */
  function loopVideo() {
    setTimeout(() => {
      videoRef?.current?.play();
    }, POD_VIDEO_LOOP_DELAY_MS);
  }

  useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.addEventListener("ended", loopVideo);
    }
    return () => {
      videoRef?.current?.removeEventListener("ended", loopVideo);
    };
  }, []);

  /**
   * Play video every time pod video is visible in viewport again
   */
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    if (podInView) {
      const promise = videoRef.current.play();
      if (promise) {
        promise.catch(e => {});
      }
    } else {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (error) {}
    }
  }, [podInView]);

  return (
    <Box
      ref={inViewRef}
      position="absolute"
      width="120px"
      height="156px"
      bottom={0}
      right={0}
      zIndex={1000}
      className={className}
      style={{
        userSelect: "none",
        pointerEvents: "none",
        ...(style || {})
      }}
    >
      <video
        preload="auto"
        playsInline
        controls={false}
        muted
        disableRemotePlayback
        ref={videoRef}
        style={{ width: "100%", height: "100%" }}
        placeholder="https://www.datocms-assets.com/15671/1632837015-pod-video-placeholder.png?auto=format,compress"
      >
        {assets.mp4 && (
          <source
            src={convertDatoCmsAssetUrlToCloudfrontUrl(assets.mp4)}
            type='video/mp4; codecs="hvc1"'
          />
        )}
        {assets.webm && (
          <source
            src={convertDatoCmsAssetUrlToCloudfrontUrl(assets.webm)}
            type="video/webm"
          />
        )}
      </video>
    </Box>
  );
};

export default PodVideo;
